/**
 * Client-side mounting for marketing pages. On first load the prerendered DOM is adopted
 * as-is (no re-render flash, no image reloads); on SPA navigation the same template renders.
 */
import './marketing.css';
import { applyHead, FONTS_HREF } from './head.js';
import { layout } from './ui.js';

// Captured at module-evaluation time, i.e. before the router clears #app for its first render.
/** @type {HTMLElement | null} */
let prerendered =
  typeof document === 'undefined' ? null : document.querySelector('#app > .mk[data-prerendered]');

function ensureFonts() {
  if (document.querySelector('link[href*="fonts.googleapis.com/css2"]')) return;
  const l = document.createElement('link');
  l.rel = 'stylesheet';
  l.href = FONTS_HREF;
  document.head.appendChild(l);
}

/** Below-the-fold `[data-reveal]` blocks settle in as they scroll into view. */
function reveal(root) {
  if (!('IntersectionObserver' in window)) return () => {};
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return () => {};
  const vh = window.innerHeight;
  const els = [...root.querySelectorAll('[data-reveal]')].filter(
    (el) => el.getBoundingClientRect().top > vh * 0.92,
  );
  for (const el of els) el.classList.add('mk-pre');
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('mk-in');
        e.target.classList.remove('mk-pre');
        io.unobserve(e.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
  );
  for (const el of els) io.observe(el);
  return () => io.disconnect();
}

/**
 * @param {HTMLElement} el
 * @param {import('./head.js').PageView} view
 * @param {(root: HTMLElement) => (void | (() => void))} [enhance]
 */
export function mountPage(el, view, enhance) {
  // Set by the prerendered index.html when it served as the SPA fallback for another URL.
  document.documentElement.removeAttribute('data-spa-shell');
  ensureFonts();
  applyHead(view);
  let root;
  if (prerendered && prerendered.dataset.prerendered === view.path) {
    root = prerendered;
    el.replaceChildren(root);
  } else {
    el.innerHTML = layout(view.path, view.body);
    root = /** @type {HTMLElement} */ (el.firstElementChild);
    if (!window.location.hash) window.scrollTo(0, 0);
  }
  prerendered = null;
  const cleanups = [reveal(root), enhance?.(root)];
  return () => {
    for (const fn of cleanups) if (typeof fn === 'function') fn();
  };
}
